import {
    Platform
} from "react-native";
import {
    getAndroidFontFamily,
    getAndroidFontWeight
} from "../../utils";

export type LocaleTypographyConstantType = {
    typography: NCoreUIKit.Typography;
    locale?: string;
};

export type ArabicLocaleConstantType = {
    locale?: string;
};

export type ArabicFontWeightConstantType = {
    fontWeight: string | number;
    fontWeights?: Array<number>;
    fontFamily: string;
};

export type ArabicTypographyRoleType = "reading" | "ui";

const ARABIC_LANGUAGE = "ar";

export const ARABIC_FONT_FAMILY = "Noto Sans Arabic";

export const ARABIC_READING_FONT_FAMILY = "Noto Naskh Arabic";

export const ARABIC_FONT_WEIGHTS = [
    300,
    400,
    500,
    600
];

export const ARABIC_READING_FONT_WEIGHTS = [
    400,
    600
];

export const ARABIC_FONT_SCALE = 1.125;

export const ARABIC_LINE_HEIGHT_RATIOS: Record<ArabicTypographyRoleType, number> = {
    reading: 1.8,
    ui: 1.5
};

export const ARABIC_FALLBACK_FONT_WEIGHT = 400;

export const TYPOGRAPHY_GRID = 4;

const READING_ROLE_PREFIX = "body";

const READING_VARIANT_PREFIX = "reading";

const NON_TEXT_ROLE_PREFIX = "mono";

const CSS_FONT_FAMILY_PATTERN = /^-?[A-Za-z_][\w-]*(?:\s+-?[A-Za-z_][\w-]*)*$/;

const arabicTypographyCache = new WeakMap<NCoreUIKit.Typography, NCoreUIKit.Typography>();

const webTypographyCache = new WeakMap<NCoreUIKit.Typography, NCoreUIKit.Typography>();

export const isArabicLocale = ({
    locale
}: ArabicLocaleConstantType): boolean => {
    if(!locale) {
        return false;
    }

    return locale.split(/[-_]/)[0]?.toLowerCase() === ARABIC_LANGUAGE;
};

export const isReadingVariant = (variant: string): boolean => {
    return variant.startsWith(READING_VARIANT_PREFIX);
};

export const getWebFontFamily = (fontFamily: string): string => {
    if(fontFamily.indexOf(",") !== -1 || fontFamily.indexOf("\"") !== -1 || CSS_FONT_FAMILY_PATTERN.test(fontFamily)) {
        return fontFamily;
    }

    return `"${fontFamily}"`;
};

export const getArabicFontWeight = ({
    fontWeights = ARABIC_FONT_WEIGHTS,
    fontFamily,
    fontWeight
}: ArabicFontWeightConstantType): number => {
    const numericWeight = Number(fontWeight);

    const sourceWeight = Number.isFinite(numericWeight)
        ? numericWeight
        : getAndroidFontWeight({
            fontFamily
        }) ?? ARABIC_FALLBACK_FONT_WEIGHT;

    return fontWeights.reduce((nearest, weight) => {
        return Math.abs(weight - sourceWeight) < Math.abs(nearest - sourceWeight) ? weight : nearest;
    });
};

const getArabicTypography = (typography: NCoreUIKit.Typography): NCoreUIKit.Typography => {
    const cached = arabicTypographyCache.get(typography);

    if(cached) {
        return cached;
    }

    const arabicTypography = {
        ...typography
    };

    (Object.keys(typography) as Array<keyof NCoreUIKit.Typography>).forEach((key) => {
        const token = typography[key];

        if(key.startsWith(NON_TEXT_ROLE_PREFIX)) {
            return;
        }

        const isReading = isReadingVariant(key);

        const role: ArabicTypographyRoleType = isReading || key.startsWith(READING_ROLE_PREFIX) ? "reading" : "ui";

        const arabicFontFamily = isReading ? ARABIC_READING_FONT_FAMILY : ARABIC_FONT_FAMILY;

        const fontWeight = getArabicFontWeight({
            fontWeights: isReading ? ARABIC_READING_FONT_WEIGHTS : ARABIC_FONT_WEIGHTS,
            fontFamily: token.fontFamily,
            fontWeight: token.fontWeight
        });

        const fontSize = Math.round(token.fontSize * ARABIC_FONT_SCALE);

        const lineHeight = Math.round(fontSize * ARABIC_LINE_HEIGHT_RATIOS[role] / TYPOGRAPHY_GRID) * TYPOGRAPHY_GRID;

        const fontStyle = Platform.select({
            web: {
                fontFamily: `${getWebFontFamily(token.fontFamily)}, "${arabicFontFamily}", ${isReading ? "serif" : "system-ui, sans-serif"}`,
                fontWeight: String(fontWeight)
            },
            android: {
                fontFamily: getAndroidFontFamily({
                    fontFamily: arabicFontFamily,
                    fontWeight
                }),
                fontWeight: "normal"
            },
            default: {
                fontWeight: String(fontWeight),
                fontFamily: arabicFontFamily
            }
        });

        arabicTypography[key] = {
            ...token,
            ...fontStyle,
            letterSpacing: 0,
            lineHeight,
            fontSize
        };
    });

    arabicTypographyCache.set(typography, arabicTypography);

    return arabicTypography;
};

const getWebTypography = (typography: NCoreUIKit.Typography): NCoreUIKit.Typography => {
    if(Platform.OS !== "web") {
        return typography;
    }

    const cached = webTypographyCache.get(typography);

    if(cached) {
        return cached;
    }

    const quotedKeys = (Object.keys(typography) as Array<keyof NCoreUIKit.Typography>).filter((key) => {
        return getWebFontFamily(typography[key].fontFamily) !== typography[key].fontFamily;
    });

    const webTypography = quotedKeys.length ? {
        ...typography
    } : typography;

    quotedKeys.forEach((key) => {
        webTypography[key] = {
            ...typography[key],
            fontFamily: getWebFontFamily(typography[key].fontFamily)
        };
    });

    webTypographyCache.set(typography, webTypography);

    return webTypography;
};

export const getLocaleTypography = ({
    typography,
    locale
}: LocaleTypographyConstantType): NCoreUIKit.Typography => {
    const webTypography = getWebTypography(typography);

    if(!isArabicLocale({
        locale
    })) {
        return webTypography;
    }

    return getArabicTypography(webTypography);
};
